<script lang="ts" generic="TItem" setup>
import VueDraggable from 'vuedraggable';
import Row from '../utils/Row.vue';
import type { Header } from '../helpers';

interface Props
{
    showHeader: boolean;
    headers: Header[];
    rows: TItem[];
    pager: Pager;
}

defineOptions({
    name: 'ListViewTableLayout',
    inheritAttrs: false
});

const props = withDefaults(defineProps<Props>(), {});
const emptyLabel = inject<string>('empty-label');
const draggable = inject<boolean>('draggable');
const scrollBar = inject<Ref<HTMLDivElement | null>>('scrollBar', null);
const emit = defineEmits<{
    (e: 'change', header: Header): void;
    (e: 'check', value: boolean): void;
}>();

const onDrag = inject<(items: TItem[]) => void>('onDrag');
const checkedAll = inject<boolean>('checkedAll', false);

const table = ref<HTMLTableElement | null>(null);
const { width: tableWidth } = useElementSize(table);

const items = computed({
    get()
    {
        return props.rows;
    },
    set(value: TItem[])
    {
        onDrag(value);
    }
});

const $attrs = useAttrs();
const attrs = (): Record<string, any> => only($attrs, 'class', 'style');
const headers = computed(() =>
{
    return props.headers
        .where(p => !p.disabled && p.visible !== false)
        .orderBy(p => p.position)
        .toArray();
});

const hideScroll = computed(() => !!scrollBar.value);

const getStyle = (header: Header): Record<string, string> =>
{
    return {
        width: header.width ? `${header.width || 0}${(header.width || 0).toString().endsWith('%') ? '' : 'px'}` : ''
    };
};

const canSort = (): boolean =>
{
    return draggable;
};

const getIcon = (header: Header): string =>
{
    return 'fa fa-fw fa-sort' + (props.pager.sorting !== (header.sort || '') ? '' : props.pager.order.toLowerCase() === 'asc' ? '-up' : '-down');
};

const onSorting = (header: Header): void =>
{
    emit('change', header);
};

const checkAllItems = (value: boolean): void =>
{
    emit('check', value);
};

const setScrollLeft = (value: number): void =>
{
    if (!table.value) return;

    table.value.parentElement.parentElement.scrollLeft = value;
};

defineExpose({
    width: tableWidth,
    setScrollLeft
});
</script>

<template>
    <Responsive :top="true" :hide-scroll="hideScroll" :enabled="true" v-bind="attrs()">
        <table ref="table" class="table table-data table-stripped mb-0" :class="{ 'table-hover': items.length > 0 }">
            <colgroup v-if="showHeader">
                <col v-for="(header, index) in headers" :key="`col-${index}`" :style="getStyle(header)">
            </colgroup>
            <thead v-if="showHeader">
                <tr>
                    <th v-for="(header, index) in headers" :key="`th-${index}`" :class="{ 'buttons': header.type == 'buttons' || false }">
                        <IdeoFormCheckbox
                            v-if="header.checkbox"
                            v-model="checkedAll"
                            @change="checkAllItems"
                        />
                        <a v-else-if="header.sort || false" href="" @click.stop.prevent="onSorting(header)">
                            <i :class="getIcon(header)" />
                            {{ header.title || '' }}
                        </a>
                        <span v-else>
                            {{ header.title || '' }}
                        </span>
                    </th>
                </tr>
            </thead>
            <VueDraggable
                v-if="items.length > 0"
                v-model="items"
                :item-key="(el: TItem, index: number) => index"
                draggable=".draggable"
                :move="canSort"
                tag="tbody"
            >
                <template #item="{ element: item, index }: { element: TItem, index: number}">
                    <Row :key="`row-${index}`" :index="index" :item="item" :headers="props.headers">
                        <slot name="row" :item="item" :index="index" />
                    </Row>
                </template>
            </VueDraggable>
            <tbody v-else>
                <tr>
                    <td :colspan="headers.length" class="px-0">
                        <div class="text-center p-3 mt-1 bg-body-tertiary">
                            {{ $t(emptyLabel) }}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </Responsive>
</template>
